import { ArrowRight } from "lucide-react";
import { Overline, PrimaryButton } from "../Shared";
import { SampleMapCanvas } from "./SampleMapCanvas";
import { SampleMapControls } from "./SampleMapControls";
import { Readout, Legend, SurveyStats, ProfileChart, CompareCard } from "./SampleMapPanels";
import { useSampleMapState } from "./useSampleMapState";

const SampleMapIntro = () => (
  <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
    <div className="max-w-2xl">
      <div className="flex flex-wrap items-center gap-3">
        <span
          data-testid="live-demo-badge"
          className="inline-flex items-center gap-2 rounded-full bg-cyan-400/15 border border-cyan-300/40 px-3 py-1 text-xs font-bold font-mono uppercase tracking-[0.2em] text-cyan-200"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-300 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-300" />
          </span>
          Live Demo
        </span>
        <Overline light>Interactive Sample Survey</Overline>
      </div>
      <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white tracking-tight">Explore a finished survey</h2>
      <p className="mt-4 text-lg text-slate-200">
        This is the kind of map you receive. Hover for live depth readings, switch layers, compare
        two survey years, click two points to measure — then download a snapshot to share.
      </p>
    </div>
    <PrimaryButton to="/request-a-quote" tone="hero" testId="sample-map-request-quote-button" className="shrink-0">
      Map my waterbody <ArrowRight className="w-4 h-4" />
    </PrimaryButton>
  </div>
);

export const SampleMapSection = () => {
  const m = useSampleMapState();
  return (
    <section id="sample-map" className="relative z-10 -mt-24 pb-4" data-testid="sample-map-anchor">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="demo-glow rounded-3xl bg-gradient-to-br from-[#082F49] via-[#0B3B5C] to-[#0A1628] p-6 sm:p-10 text-slate-200" data-testid="sample-map-section">
          <SampleMapIntro />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-4">
              <SampleMapControls
                layer={m.layer} setLayer={m.setLayer}
                showTransects={m.showTransects} setShowTransects={m.setShowTransects}
                unit={m.unit} setUnit={m.setUnit}
                hasPoints={m.points.length > 0} clearPoints={m.clearPoints}
                compare={m.compare} toggleCompare={m.toggleCompare}
                onExport={m.onExport} exporting={m.exporting}
              />
              <SampleMapCanvas
                layer={m.layer} showTransects={m.showTransects} unit={m.unit}
                points={m.points} compare={m.compare} split={m.split} setSplit={m.setSplit}
                onHover={m.setHover} onPick={m.onPick}
              />
              <p className="sm:hidden font-mono text-[11px] uppercase tracking-wider text-slate-300" data-testid="sample-map-label-mobile">
                Sample Survey: Clearwater Reservoir <span className="text-amber-300">(demo data)</span>
              </p>
            </div>
            <div className="lg:col-span-4 space-y-4">
              <Readout hover={m.hover} unit={m.unit} compare={m.compare} />
              {m.compare && <CompareCard stats={m.stats} />}
              <ProfileChart points={m.points} unit={m.unit} />
              <Legend layer={m.layer} />
              <SurveyStats stats={m.stats} unit={m.unit} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
